import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Collapse,
} from '@mui/material';
import {
  Search,
  HomeWork as HomeWorkIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import { GroupHeader } from '../../../../../../shared/components/grouped-view';
import { PlotPreview } from '../../../../api/sitesApi';
import { formatCurrency } from '../../../../../../shared/utils/currency';

const AUTO_EXPAND_COUNT = 3;

interface PlotPreviewAccordionProps {
  plots: PlotPreview[];
}

const PlotPreviewAccordion: React.FC<PlotPreviewAccordionProps> = ({
  plots,
}) => {
  const [filterText, setFilterText] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const hasAutoExpanded = useRef(false);

  // Auto-expand first N plots on mount
  useEffect(() => {
    if (plots.length > 0 && !hasAutoExpanded.current) {
      hasAutoExpanded.current = true;
      setExpanded(
        new Set(
          plots.slice(0, AUTO_EXPAND_COUNT).map(p => `plot-${p.plotNumber}`)
        )
      );
    }
  }, [plots]);

  const toggle = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const filteredPlots = filterText
    ? plots.filter(
        p =>
          p.plotNumber.toLowerCase().includes(filterText.toLowerCase()) ||
          p.name.toLowerCase().includes(filterText.toLowerCase()) ||
          p.labourItems.some(li =>
            li.name.toLowerCase().includes(filterText.toLowerCase())
          )
      )
    : plots;

  return (
    <Box data-testid="plot-preview">
      {plots.length > 5 && (
        <TextField
          size="small"
          placeholder="Filter plots..."
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
          sx={{ mb: 2 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      )}

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {filteredPlots.map(plot => {
          const plotKey = `plot-${plot.plotNumber}`;
          const isExpanded = expanded.has(plotKey);
          const plotTotal = plot.labourItems.reduce(
            (sum, item) => sum + item.value,
            0
          );

          return (
            <Box key={plotKey}>
              <GroupHeader
                icon={HomeWorkIcon}
                label={`${plot.plotNumber} — ${plot.name}`}
                count={plot.labourItems.length}
                expanded={isExpanded}
                onToggle={() => toggle(plotKey)}
                secondaryText={
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      fontSize: '0.9375rem',
                      whiteSpace: 'nowrap',
                      color: 'text.secondary',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {formatCurrency(plotTotal)}
                  </Typography>
                }
              />
              <Collapse in={isExpanded} unmountOnExit>
                <Box>
                  {plot.labourItems.map((item, idx) => (
                    <Box
                      key={`${plot.plotNumber}-${item.name}-${idx}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        pl: { xs: 3.5, sm: 4 },
                        pr: { xs: 1.5, sm: 2 },
                        py: 0.75,
                        minHeight: 36,
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <BuildIcon
                        sx={{
                          fontSize: 16,
                          color: 'text.secondary',
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, flex: 1 }}
                      >
                        {item.name}
                      </Typography>
                      {item.isMissingDefinition && (
                        <Chip
                          label="New"
                          size="small"
                          color="warning"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          fontSize: '0.9375rem',
                          whiteSpace: 'nowrap',
                          color: 'text.secondary',
                          fontVariantNumeric: 'tabular-nums',
                          minWidth: 70,
                          textAlign: 'right',
                        }}
                      >
                        {formatCurrency(item.value)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </Box>
          );
        })}

        {filteredPlots.length === 0 && (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No plots match your filter
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PlotPreviewAccordion;
